import type { ChecklistSubsetPolicy } from '@tcvs/shared';
import type { ChecklistParallelMatch } from '../shared/types';
import { parallelDefinitionLabel } from './checklist-parallel-selection';

export function ChecklistParallelFallback({
  match,
  policy,
}: {
  match: ChecklistParallelMatch | undefined;
  policy?: ChecklistSubsetPolicy;
}): JSX.Element | null {
  if (match === undefined || match.tier === 1) return null;
  if (match.tier === 2) {
    return (
      <p className="tcvs-checklist-parallel-advisory" role="note">
        These parallels are known for the {match.subset} subset, but this exact card is not
        confirmed in the imported roster. These choices are research only. Use an explicit manual
        override for independently reviewed facts; no verified source lineage is established.
        {policy?.plainVersion.decision === 'requires-parallel'
          ? ' This subset has no plain version.'
          : policy?.plainVersion.decision === 'unknown' || !policy
            ? ' Current plain-version policy is unavailable.'
            : ' A plain version exists in the source, but this card still has no roster match.'}
      </p>
    );
  }
  if (match.groups.length === 0) return null;
  return (
    <details className="tcvs-checklist-parallel-browser">
      <summary>Browse all known parallels for this set</summary>
      <p className="tcvs-muted">
        These are general product definitions, not confirmed matches for this card. They are grouped
        by subset for reference only.
      </p>
      {match.identifiedSubset !== undefined ? (
        <p className="tcvs-checklist-parallel-mismatch">
          No exact source definition matched subset &quot;{match.identifiedSubset}&quot;. Check the
          source naming before relying on another group.
        </p>
      ) : null}
      <div className="tcvs-checklist-parallel-groups">
        {match.groups.map((group) => (
          <section key={group.subset}>
            <h4>{group.subset}</h4>
            <ul>
              {group.definitions.map((definition) => (
                <li key={definition.definitionId}>{parallelDefinitionLabel(definition)}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </details>
  );
}
